import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VapiClient } from "@/lib/vapi-client";
import { extractFramesFromVideo } from "@/lib/video-frame-extractor";
import { Briefcase, Phone, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Gauge from "@/components/Gauge";
import { supabase } from "@/lib/supabase-client";
import { calculateTokensForDuration } from "@/lib/token-utils";
import { deductTokens } from "@/lib/tokens-api";

interface TranscriptMessage {
  role: 'user' | 'assistant';
  content: string;
}

export default function InterviewSessionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vapiClientRef = useRef<VapiClient | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const videoMimeTypeRef = useRef<string>('video/webm');
  const [running, setRunning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sessionDuration, setSessionDuration] = useState(15);
  const [status, setStatus] = useState("Starting your interview...");
  const [vapiConnected, setVapiConnected] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [moodScore, setMoodScore] = useState<number | null>(50);
  const [moodLoading, setMoodLoading] = useState(false);
  const lastAnalyzedCountRef = useRef<number>(0);
  const [isStarting, setIsStarting] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [customAssistantId, setCustomAssistantId] = useState<string | null>(null);
  const [systemPrompt, setSystemPrompt] = useState<string | null>(null);
  const [jobInfo, setJobInfo] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const autoStartRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const cleanupRef = useRef(false);
  const [interviewerSpeaking, setInterviewerSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const sessionIdParam = searchParams.get('sessionId');
    const assistantIdParam = searchParams.get('assistantId');

    if (sessionIdParam) {
      console.log('[Interview] Loading session:', sessionIdParam);
      setSessionId(sessionIdParam);
      if (assistantIdParam && assistantIdParam !== 'undefined' && assistantIdParam !== 'null') {
        setCustomAssistantId(assistantIdParam);
      }

      const storedPrompt = sessionStorage.getItem('interviewSystemPrompt');
      const storedJobInfo = sessionStorage.getItem('interviewJobInfo');
      const storedQuestions = sessionStorage.getItem('interviewQuestions');

      if (storedPrompt) {
        setSystemPrompt(storedPrompt);
        console.log('[Interview] Using custom prompt for job interview');
      }

      if (storedJobInfo) {
        try {
          const jobData = JSON.parse(storedJobInfo);
          setJobInfo(jobData);
          // Get duration from job configuration
          const duration = jobData.interview_configurations?.max_duration_minutes || jobData.duration_minutes || 5;
          const durationSeconds = duration * 60;
          setSessionDuration(durationSeconds);
          setCountdown(durationSeconds);
          console.log('[Interview] Job info loaded:', jobData);
          console.log('[Interview] Interview duration:', duration, 'minutes');

          // Check both possible locations for voice_gender
          const voiceGender = jobData.voiceGender || jobData.interview_configurations?.voice_gender;
          console.log('[Interview] Voice gender:', voiceGender, {
            fromTopLevel: jobData.voiceGender,
            fromConfig: jobData.interview_configurations?.voice_gender
          });
        } catch (err) {
          console.error('Error parsing job info:', err);
        }
      } else {
        console.log('[Interview] No job info found in sessionStorage');
      }

      if (storedQuestions) {
        try {
          const questionsData = JSON.parse(storedQuestions);
          setQuestions(questionsData);
          console.log('[Interview] Questions loaded:', questionsData.length);
        } catch (err) {
          console.error('Error parsing questions:', err);
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (!autoStartRef.current && !isStarting && !vapiConnected && sessionId) {
      autoStartRef.current = true;
      begin();
    }
  }, [sessionId, isStarting, vapiConnected]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      setCountdown(t => t - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [running]);

  useEffect(() => {
    if (running && countdown <= 0) {
      console.log('[Interview] Timer expired, ending session...');
      endSession();
    }
  }, [countdown, running]);

  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (sessionId && running) {
        console.log('[Interview] Page closing, marking session as completed...');
        const actualDuration = sessionDuration - countdown;
        const transcriptText = transcript
          .map(msg => `${msg.role === 'user' ? 'Candidate' : 'Interviewer'}: ${msg.content}`)
          .join('\n\n');

        navigator.sendBeacon(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/interview_sessions?id=eq.${sessionId}`,
          JSON.stringify({
            status: 'completed',
            ended_at: new Date().toISOString(),
            duration_seconds: actualDuration,
            transcript: transcriptText || ''
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionId, running, countdown, transcript, sessionDuration]);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        console.log('[Interview] Already cleaned up, skipping');
        return;
      }

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        console.log('[Interview] First mount cleanup (StrictMode), skipping');
        return;
      }

      console.log('[Interview] Component unmounting, performing final cleanup...');
      cleanupRef.current = true;

      if (sessionId && running && !isProcessing) {
        const actualDuration = sessionDuration - countdown;
        const transcriptText = transcript
          .map(msg => `${msg.role === 'user' ? 'Candidate' : 'Interviewer'}: ${msg.content}`)
          .join('\n\n');

        supabase
          .from('interview_sessions')
          .update({
            status: 'completed',
            ended_at: new Date().toISOString(),
            duration_seconds: actualDuration,
            transcript: transcriptText || ''
          })
          .eq('id', sessionId)
          .then(({ error }) => {
            if (error) console.error('[Interview] Failed to complete session on unmount:', error);
            else console.log('[Interview] Session marked as completed on unmount');
          });
      }

      stopAllMedia();
    };
  }, []);

  const addMessage = (text: string, role: 'user' | 'assistant') => {
    setTranscript(prev => {
      const newTranscript = [...prev, { role, content: text }];

      if (role === 'user') {
        const userMessageCount = newTranscript.filter(m => m.role === 'user').length;
        if (userMessageCount >= 2 && userMessageCount % 2 === 0 && userMessageCount !== lastAnalyzedCountRef.current) {
          lastAnalyzedCountRef.current = userMessageCount;
          fetchMoodScore(newTranscript);
        }
      }

      return newTranscript;
    });
  };

  const fetchMoodScore = async (messages: TranscriptMessage[]) => {
    const conversationMessages = messages.filter(msg =>
      msg.content && msg.content.trim().length > 0
    );

    if (conversationMessages.length < 2) {
      return null;
    }

    try {
      setMoodLoading(true);
      console.log('[Mood] Fetching mood score from API...');

      const { edgeFunctions } = await import('@/lib/edge-functions');
      const transcript = conversationMessages.map(msg => `${msg.role}: ${msg.content}`).join('\n');
      const moodData = await edgeFunctions.analyzeMood(transcript);
      console.log('[Mood] Mood score received:', moodData);
      setMoodScore(moodData.score || moodData.overall_score);
      return moodData;
    } catch (err) {
      console.error('[Mood] Failed to fetch mood score:', err);
      return null;
    } finally {
      setMoodLoading(false);
    }
  };

  async function startVideoRecording(stream: MediaStream) {
    try {
      console.log('[Recording] Starting video recording...');

      let mimeType = 'video/webm;codecs=vp8,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];
      videoMimeTypeRef.current = mimeType;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000);
      setRecording(true);
    } catch (error) {
      console.error('[Recording] Failed to start recording:', error);
    }
  }

  function stopAllMedia(): void {
    console.log('[Interview] Stopping all media streams...');

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
        console.log('[Interview] Media recorder stopped');
      } catch (err) {
        console.error('[Interview] Error stopping media recorder:', err);
      }
    }

    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('[Interview] Stopped track:', track.kind);
      });
      videoRef.current.srcObject = null;
      console.log('[Interview] Camera and microphone stopped');
    }

    if (vapiClientRef.current) {
      try {
        vapiClientRef.current.destroy();
        vapiClientRef.current = null;
        console.log('[Interview] Vapi client destroyed');
      } catch (err) {
        console.error('[Interview] Error destroying Vapi client:', err);
      }
    }

    setRecording(false);
  }

  async function stopVideoRecording(): Promise<Blob | null> {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(null);
        return;
      }

      mediaRecorder.onstop = () => {
        const mimeType = videoMimeTypeRef.current;
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        setRecording(false);
        resolve(blob);
      };

      // Stop the recorder
      mediaRecorder.stop();

      // IMMEDIATELY stop camera and microphone tracks after stopping recorder
      // The recorded data is already in recordedChunksRef, so this won't affect the video
      if (videoRef.current && videoRef.current.srcObject) {
        console.log('[Recording] IMMEDIATELY stopping camera/mic tracks');
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('[Recording] Stopped track:', track.kind);
        });
        videoRef.current.srcObject = null;
      }
    });
  }

  async function begin() {
    console.log('[Interview] Starting interview...');

    if (isStarting || vapiConnected) {
      return;
    }

    setIsStarting(true);

    try {
      setError(null);

      // Deduct tokens FIRST before starting anything
      const durationMinutes = Math.ceil(sessionDuration / 60);
      const tokensToDeduct = calculateTokensForDuration(durationMinutes);

      console.log('[Interview] Attempting to deduct tokens:', {
        durationMinutes,
        tokensToDeduct,
        sessionId
      });

      if (sessionId) {
        try {
          setStatus("Processing payment...");
          const result = await deductTokens(
            tokensToDeduct,
            sessionId,
            `Interview session: ${durationMinutes} minutes`
          );
          console.log(`[Interview] Successfully deducted ${tokensToDeduct} tokens. New balance: ${result.newBalance}`);
        } catch (error: any) {
          console.error('[Interview] Token deduction failed:', error);
          setError(error.message || 'Failed to process payment');
          setStatus('Payment failed. Please try again.');
          setIsStarting(false);

          // Show user-friendly error
          if (error.message?.includes('Insufficient tokens')) {
            alert('You do not have enough coins to start this interview. Please purchase more coins from the pricing page.');
          } else {
            alert('Failed to process payment: ' + error.message);
          }
          return;
        }
      } else {
        console.warn('[Interview] No sessionId available - cannot deduct tokens');
      }

      setStatus("Connecting to interview coach...");

      if (vapiClientRef.current) {
        vapiClientRef.current.destroy();
        vapiClientRef.current = null;
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log('[Interview] Fetching Vapi configuration from Supabase...');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const vapiConfigResponse = await fetch(
        `${supabaseUrl}/functions/v1/get-vapi-config`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
        }
      );

      if (!vapiConfigResponse.ok) {
        const errorData = await vapiConfigResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch Vapi configuration');
      }

      const vapiConfig = await vapiConfigResponse.json();

      console.log('[Interview] Raw vapi config response:', {
        fullConfig: vapiConfig,
        publicKey: vapiConfig.publicKey,
        assistantIds: vapiConfig.assistantIds,
        customAssistantId: customAssistantId,
        customAssistantIdLength: customAssistantId?.length || 0,
      });

      const publicKey = vapiConfig.publicKey;

      // Determine the correct voice gender
      const voiceGender = jobInfo?.voiceGender || jobInfo?.interview_configurations?.voice_gender || 'male';
      console.log('[Interview] Using voice gender:', voiceGender);

      // Select assistant based on voice gender
      const fallbackAssistantId = voiceGender === 'female'
        ? vapiConfig.assistantIds.female
        : vapiConfig.assistantIds.male;

      const assistantId = customAssistantId || fallbackAssistantId;

      console.log('[Interview] Vapi credentials check:', {
        hasPublicKey: !!publicKey,
        hasAssistantId: !!assistantId,
        publicKeyLength: publicKey?.length || 0,
        assistantIdLength: assistantId?.length || 0,
        publicKeyPreview: publicKey ? `${publicKey.substring(0, 8)}...` : 'none',
        assistantIdPreview: assistantId ? `${assistantId.substring(0, 8)}...` : 'none'
      });

      if (!publicKey) {
        throw new Error('Vapi public key not configured in Supabase secrets.');
      }

      if (!assistantId) {
        throw new Error('Vapi assistant ID not configured in Supabase secrets.');
      }

      if (publicKey.length < 20) {
        throw new Error('Vapi public key appears to be invalid (too short).');
      }

      if (assistantId.length < 20) {
        throw new Error('Vapi assistant ID appears to be invalid (too short).');
      }

      console.log('[Interview] Using assistant ID:', assistantId);
      console.log('[Interview] System prompt length:', systemPrompt?.length || 0);
      console.log('[Interview] Has custom prompt:', !!systemPrompt);
      console.log('[Interview] Questions to ask:', questions.length);

      let finalSystemPrompt = systemPrompt;

      // If we have custom questions, append them to the system prompt
      if (questions && questions.length > 0) {
        const questionsText = questions.map((q: any, idx: number) =>
          `Question ${idx + 1}: ${q.question}${q.expected_answer ? `\nExpected answer context: ${q.expected_answer}` : ''}`
        ).join('\n\n');

        finalSystemPrompt = `${systemPrompt}\n\nIMPORTANT: You must ask these specific questions during the interview:\n\n${questionsText}\n\nAsk these questions during the interview. Wait for the candidate's response before moving to the next question.`;
        console.log('[Interview] Enhanced prompt with', questions.length, 'questions');
      }

      const assistantOverrides = finalSystemPrompt ? {
        model: {
          provider: 'openai' as const,
          model: 'gpt-4',
          messages: [{
            role: "system" as const,
            content: finalSystemPrompt
          }]
        }
      } : undefined;

      if (assistantOverrides) {
        console.log('[Interview] Using assistant overrides with custom prompt');
      } else {
        console.log('[Interview] No assistant overrides - using default assistant behavior');
      }

      const client = new VapiClient({
        publicKey,
        assistantId,
        assistantOverrides,
        onCallStart: () => {
          console.log('[Vapi] Call started - tokens already deducted');
          setVapiConnected(true);
          setStatus("Connected! Interview in progress...");
          setRunning(true);
        },
        onCallEnd: () => {
          console.log('[Vapi] Call ended by Vapi (user request or profanity detected)');
          setVapiConnected(false);

          // Vapi ended the call - process the session
          if (running && !isProcessing) {
            console.log('[Vapi] Triggering session end due to Vapi call termination');
            endSession();
          }
        },
        onMessage: (message: any) => {
          // console.log('[Vapi] Message:', message);

          if (message.type === 'transcript' && message.transcriptType === 'final') {
            const text = message.transcript;
            const role = message.role;

            if (text && role) {
              addMessage(text, role);
              if (role === 'assistant') {
                setInterviewerSpeaking(true);
                setTimeout(() => setInterviewerSpeaking(false), 2000);
              }
            }
          }
        },
        onError: (error: Error) => {
          console.error('[Vapi] Error:', error);
          setError(error.message || 'Connection error');
          setStatus("Error occurred. Please refresh and try again.");
          stopAllMedia();
        }
      });

      vapiClientRef.current = client;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      await startVideoRecording(stream);

      await client.start();
      console.log('[Interview] Vapi client started successfully');

    } catch (err: any) {
      console.error('[Interview] Failed to start:', err);

      let errorMessage = 'Failed to start interview';
      let statusMessage = 'Failed to start. Please try again.';

      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        errorMessage = 'Camera and microphone access denied';
        statusMessage = 'Please allow camera and microphone access in your browser settings and refresh the page.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera or microphone found';
        statusMessage = 'Please connect a camera and microphone to continue.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera or microphone already in use';
        statusMessage = 'Please close other applications using your camera/microphone and try again.';
      } else {
        errorMessage = err.message || 'Failed to start interview';
        statusMessage = 'Failed to start. Please check your camera and microphone permissions.';
      }

      setError(errorMessage);
      setStatus(statusMessage);
      setIsStarting(false);
      stopAllMedia();
    }
  }

  async function endSession() {
    console.log('[Interview] Ending session...');

    // Prevent multiple calls to endSession
    if (isProcessing) {
      console.log('[Interview] Already processing, skipping duplicate call');
      return;
    }

    // Tokens already deducted upfront when interview started
    const actualDuration = sessionDuration - countdown;
    const durationMinutes = Math.ceil(actualDuration / 60);
    console.log(`[Interview] Interview lasted ${durationMinutes} minutes (tokens already deducted upfront)`);

    setRunning(false);
    setIsProcessing(true);
    setStatus("Processing your interview...");

    try {
      // Stop video recording (this also stops camera/mic immediately)
      console.log('[Interview] Stopping video recording and camera/mic...');
      const videoBlob = await stopVideoRecording();

      // Only stop Vapi if still connected
      if (vapiClientRef.current && vapiConnected) {
        console.log('[Interview] Stopping Vapi client (user initiated)...');
        vapiClientRef.current.stop();
        vapiClientRef.current.destroy();
        vapiClientRef.current = null;
      } else if (vapiClientRef.current) {
        console.log('[Interview] Cleaning up Vapi client (already disconnected)...');
        vapiClientRef.current.destroy();
        vapiClientRef.current = null;
      }

      const transcriptText = transcript
        .map(msg => `${msg.role === 'user' ? 'Candidate' : 'Interviewer'}: ${msg.content}`)
        .join('\n\n');

      if (sessionId && transcriptText) {
        let videoFrames: string[] = [];

        if (videoBlob && videoBlob.size > 0) {
          try {
            console.log('[Interview] Extracting video frames for analysis...');
            setStatus("Processing video...");
            const actualDuration = sessionDuration - countdown;
            const frameBlobs = await extractFramesFromVideo(videoBlob, 3, actualDuration);

            console.log('[Interview] Converting frames to base64...');
            videoFrames = await Promise.all(
              frameBlobs.map(async (blob) => {
                return new Promise<string>((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.readAsDataURL(blob);
                });
              })
            );

            console.log('[Interview] Extracted', videoFrames.length, 'video frames');
          } catch (error) {
            console.error('[Interview] Failed to extract video frames:', error);
          }
        }

        // Check if interview was at least 50% complete
        const actualDuration = sessionDuration - countdown;
        const completionPercentage = (actualDuration / sessionDuration) * 100;
        const isHalfwayComplete = completionPercentage >= 50;

        console.log(`[Interview] Interview completion: ${completionPercentage.toFixed(1)}% (${actualDuration}s / ${sessionDuration}s)`);

        let analysisData = null;

        if (isHalfwayComplete) {
          console.log('[Interview] Interview at least 50% complete - generating AI analysis...');
          setStatus("Analyzing your performance...");

          try {
            const { edgeFunctions } = await import('@/lib/edge-functions');
            analysisData = await edgeFunctions.gradeSession({
              transcript: transcriptText,
              videoFrames: videoFrames.length > 0 ? videoFrames : undefined,
              jobTitle: jobInfo?.title,
              jobDescription: jobInfo?.description,
            });
          } catch (gradingError: any) {
            console.error('[Interview] Error grading session:', gradingError);

            if (gradingError.message?.includes('INSUFFICIENT_PARTICIPATION')) {
              console.log('[Interview] Insufficient candidate participation detected');
              setStatus("Interview completed without scoring due to insufficient responses");
            } else {
              console.log('[Interview] Grading failed, continuing without analysis');
              setStatus("Interview completed (analysis unavailable)");
            }
          }
        } else {
          console.log('[Interview] Interview less than 50% complete - skipping scoring');
          setStatus("Interview too short for scoring...");
        }

        console.log('[Interview] Completing session...');
        const ended_at = new Date().toISOString();

        // Calculate tokens that were deducted (based on planned duration)
        const plannedMinutes = Math.ceil(sessionDuration / 60);
        const tokensDeducted = calculateTokensForDuration(plannedMinutes);

        const { error: updateError } = await supabase
          .from('interview_sessions')
          .update({
            status: isHalfwayComplete ? 'completed' : 'incomplete',
            ended_at,
            transcript: transcriptText || '',
            ai_evaluation: analysisData || {},
            duration_seconds: sessionDuration - countdown,
            tokens_deducted: tokensDeducted
          })
          .eq('id', sessionId);

        if (updateError) {
          console.error('[Interview] Error completing session:', updateError);
          throw new Error('Failed to complete session');
        }

        console.log('[Interview] Analysis complete, redirecting to results...');
        sessionStorage.removeItem('interviewSystemPrompt');
        sessionStorage.removeItem('interviewJobInfo');
        sessionStorage.removeItem('interviewQuestions');

        await new Promise(resolve => setTimeout(resolve, 500));
        navigate(`/interviews/${sessionId}`);
      } else {
        setStatus("Interview ended. No session to save.");
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('[Interview] Error ending session:', err);
      setError('Failed to save interview results');
      setStatus("Error saving interview. Please try again.");
      setIsProcessing(false);
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center space-y-8 animate-fade-in-up">
          <div className="relative inline-block">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto shadow-2xl">
              <Briefcase className="w-16 h-16 text-white" strokeWidth={2.5} />
            </div>
            <div className="absolute inset-0 w-32 h-32 border-4 border-green-200 rounded-full animate-spin mx-auto" style={{ borderTopColor: 'transparent', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}></div>
          </div>

          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-gray-900">Analyzing Your Interview</h1>
            <p className="text-lg text-gray-600">Hang tight while we process your responses...</p>
            <div className="flex justify-center gap-2 pt-4">
              <div className="w-3 h-3 rounded-full bg-green-600 animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-3 h-3 rounded-full bg-green-600 animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-3 h-3 rounded-full bg-green-600 animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col lg:flex-row overflow-hidden">
      {/* Videos Section - Stacks vertically on mobile, side-by-side on desktop */}
      <div className="flex-1 flex flex-col lg:flex-row p-2 sm:p-4 gap-2 sm:gap-4 min-w-0">
        {/* User Video */}
        <div className="relative h-48 sm:h-64 lg:flex-1 bg-gray-900 rounded-lg overflow-hidden border-2 border-green-500">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-black/70 backdrop-blur-sm px-2 sm:px-3 py-1 rounded text-xs sm:text-sm text-white">
            You
          </div>
          {recording && (
            <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex items-center gap-1 sm:gap-2 px-2 py-1 bg-red-600 text-white rounded text-xs font-medium">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
              REC
            </div>
          )}
        </div>

        {/* AI Interviewer Video */}
        <div className="relative h-48 sm:h-64 lg:flex-1 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-700">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(/interviewer-${
              jobInfo?.voiceGender === 'female' || jobInfo?.interview_configurations?.voice_gender === 'female'
                ? 'female'
                : 'male'
            }.jpg)` }}
          ></div>
          <div className={`absolute inset-0 transition-all duration-300 ${interviewerSpeaking ? 'ring-4 ring-green-500' : ''}`}></div>
          <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 bg-black/70 backdrop-blur-sm px-2 sm:px-3 py-1 rounded text-xs sm:text-sm text-white">
            AI Interviewer
          </div>
          {vapiConnected && (
            <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex items-center gap-1 sm:gap-2 px-2 py-1 bg-green-600 text-white rounded text-xs font-medium">
              <Mic className="w-3 h-3" />
              Live
            </div>
          )}
        </div>
      </div>

      {/* Controls & Transcript Section - Full width on mobile, sidebar on desktop */}
      <div className="w-full lg:w-96 flex-shrink-0 flex flex-col border-t lg:border-t-0 lg:border-l border-gray-700 bg-gray-800 max-h-[50vh] lg:max-h-screen lg:h-screen">
        {/* End Call Button & Performance Score - Stacked vertically */}
        <div className="flex flex-col border-b border-gray-700 flex-shrink-0">
          {/* End Call Button */}
          <div className="p-2 sm:p-4 border-b border-gray-700">
            <Button
              onClick={endSession}
              disabled={!running}
              className="w-full bg-red-600 hover:bg-red-700 text-white h-10 sm:h-11 text-sm sm:text-base touch-manipulation"
            >
              <Phone className="w-4 h-4 mr-2" />
              End Call
            </Button>
          </div>

          {/* Performance Score Card */}
          <div className="p-2 sm:p-4">
            <Card className="p-2 sm:p-4 bg-gray-750 border-gray-600 h-full">
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-xs sm:text-sm font-semibold text-white">Performance</h3>
                {moodLoading ? (
                  <div className="flex items-center gap-2 justify-center">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs sm:text-sm text-gray-400">Analyzing...</span>
                  </div>
                ) : (
                  <div className="scale-100 origin-center">
                    <Gauge label="" value={moodScore || 0} max={100} isDarkMode={true} />
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-gray-600">
                  <span className="text-xs text-gray-400">Time Left</span>
                  <span className="text-lg sm:text-xl font-bold text-green-500">{formatTime(countdown)}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Transcript Header */}
        <div className="p-2 sm:p-4 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-sm sm:text-base font-semibold text-white flex items-center justify-between">
            <span>Transcript</span>
            <span className="text-xs text-gray-400 font-normal">{transcript.length} msgs</span>
          </h3>
        </div>

        {/* Transcript Messages - Scrollable */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-2 sm:space-y-3">
          {transcript.length === 0 && (
            <p className="text-gray-500 text-xs sm:text-sm italic text-center py-4 sm:py-8">Conversation will appear here...</p>
          )}
          {transcript.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 ${
                msg.role === 'user'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-100'
              }`}>
                <p className="text-xs font-medium mb-1 opacity-70">
                  {msg.role === 'user' ? 'You' : 'AI Interviewer'}
                </p>
                <p className="text-xs sm:text-sm leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>
      </div>

      {error && (
        <div className="fixed bottom-2 sm:bottom-4 left-2 right-2 sm:left-auto sm:right-4 bg-red-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg shadow-2xl max-w-md z-50 border-2 border-red-400">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-xs sm:text-sm mb-1">{error}</p>
              {status && <p className="text-xs opacity-90 break-words">{status}</p>}
            </div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 ml-2 hover:bg-red-700 rounded p-1 transition-colors touch-manipulation"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
